import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { allVideos, videoCategories } from '@/mocks/videos';
import { getArticleBySlug, articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const video = allVideos.find((v) => v.slug === slug);
  const article = getArticleBySlug(slug || '');

  const item = video || article;
  const isVideo = !!video;

  if (!item) {
    const schema404 = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '页面未找到 - 蘑菇视频',
      url: `https://www.mogu-video.com/article/${slug}`,
    };

    return (
      <>
        <SEOMeta
          title="页面未找到"
          description="抱歉，您访问的页面不存在。请返回首页浏览更多精彩视频内容。"
          keywords="蘑菇视频,在线视频"
          canonicalUrl={`/article/${slug}`}
          schema={schema404}
        />
        <Navbar />
        <div className="pt-24 pb-20 text-center min-h-[60vh] flex flex-col items-center justify-center">
          <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-background-200 mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">内容未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您访问的内容不存在或已被移除</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const categoryInfo = videoCategories.find((c) => c.slug === item.category);
  const relatedVideos = allVideos.filter((v) => v.category === item.category && v.slug !== item.slug).slice(0, 4);
  const relatedArticles = articles.filter((a) => a.slug !== item.slug).slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: item.title,
    description: 'excerpt' in item ? item.excerpt : item.description,
    image: 'coverImage' in item ? item.coverImage : item.thumbnail,
    datePublished: item.date,
    author: {
      '@type': 'Person',
      name: 'author' in item ? item.author : '蘑菇视频编辑',
    },
    publisher: {
      '@type': 'Organization',
      name: '蘑菇视频',
      url: 'https://www.mogu-video.com',
    },
    url: `https://www.mogu-video.com/article/${item.slug}`,
  };

  return (
    <>
      <SEOMeta
        title={`${item.title} - 蘑菇视频`}
        description={'excerpt' in item ? item.excerpt : item.description}
        keywords={`${item.title},蘑菇视频,${item.tags?.join(',') || ''},在线视频`}
        canonicalUrl={`/article/${item.slug}`}
        ogType="article"
        ogImage={'coverImage' in item ? item.coverImage : item.thumbnail}
        schema={articleSchema}
      />

      <Navbar />

      <main className="pt-20 pb-16">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryInfo?.name || item.category, href: `/category/${item.category}` },
              { label: item.title },
            ]}
          />

          <article className="mt-8">
            <div className="max-w-[860px] mx-auto">
              {/* Header */}
              <header>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {item.tags?.map((tag: string) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                      {tag}
                    </span>
                  ))}
                  <span className="text-xs text-foreground-400">{item.date}</span>
                  {isVideo && (
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {'views' in item ? `${(item.views / 10000).toFixed(1)}万次观看` : ''}
                    </span>
                  )}
                  {'readTime' in item && (
                    <span className="text-xs text-foreground-400">阅读约{item.readTime}</span>
                  )}
                </div>

                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 leading-snug">
                  {item.title}
                </h1>
              </header>

              {/* Cover / Thumbnail */}
              <div className="mt-8 relative overflow-hidden rounded-xl aspect-video bg-background-200">
                <img
                  src={'coverImage' in item ? item.coverImage : item.thumbnail}
                  alt={item.title}
                  title={`${item.title} - 蘑菇视频`}
                  className="w-full h-full object-cover"
                />
                {isVideo && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group cursor-pointer">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 transition-colors">
                      <i className="ri-play-fill text-white text-3xl"></i>
                    </div>
                  </div>
                )}
                {isVideo && 'duration' in item && (
                  <span className="absolute bottom-3 right-3 bg-black/75 text-white text-sm px-3 py-1 rounded-md">
                    {item.duration}
                  </span>
                )}
              </div>

              {/* Content */}
              {'content' in item && item.content ? (
                <div
                  className="mt-10 prose prose-sm max-w-none text-foreground-800 leading-relaxed [&>p]:mb-5 [&>p]:text-[15px] [&>p]:leading-7 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:font-heading [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:text-foreground-900"
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />
              ) : (
                <div className="mt-10">
                  <p className="text-[15px] text-foreground-700 leading-7">
                    {item.description}
                  </p>
                  {isVideo && 'rating' in item && (
                    <div className="mt-6 flex items-center gap-6 p-5 rounded-xl bg-background-100 border border-background-200/70">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-primary-600 font-heading">{item.rating}</p>
                        <p className="text-xs text-foreground-500 mt-1">评分</p>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`text-lg ${i < Math.round(item.rating / 2) ? 'ri-star-fill text-amber-500' : 'ri-star-line text-foreground-300'}`}
                            ></i>
                          ))}
                        </div>
                        <p className="text-xs text-foreground-400 mt-1.5">
                          {'views' in item ? `${(item.views / 10000).toFixed(1)}万次观看` : ''} · {item.date}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </article>

          {/* Related Videos */}
          {relatedVideos.length > 0 && (
            <section className="mt-16">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">
                相关视频推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            </section>
          )}

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-12">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedArticles.map((a) => (
                  <Link key={a.id} to={`/article/${a.slug}`} className="group cursor-pointer">
                    <div className="relative overflow-hidden rounded-lg aspect-[4/3] bg-background-200">
                      <img
                        src={a.coverImage}
                        alt={a.title}
                        title={`${a.title} - 蘑菇视频`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="mt-2.5 text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {a.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-400">{a.date}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}